/**
 * DataTable.js
 *
 * Generic table component with sorting, filtering, and pagination
 * Base class for role-specific table implementations
 */

import React, { useState, useMemo } from 'react';
import './Tables.css';

/**
 * DataTable Component
 *
 * Props:
 * - columns: Array of column definitions
 *   Each: { key: string, label: string, render?: function, sortable?: boolean }
 * - data: Array of row data objects
 * - loading: Loading state (boolean)
 * - onRowClick: Row click handler (optional)
 * - pageSize: Rows per page (default: 10)
 * - filterable: Enable filtering (default: true)
 * - sortable: Enable sorting (default: true)
 * - disablePagination: Disable table pagination (default: false)
 * - className: Additional CSS classes
 */
const DataTable = ({
  columns = [],
  data = [],
  loading = false,
  onRowClick = null,
  pageSize = 10,
  filterable = true,
  sortable = true,
  disablePagination = false,
  className = ''
}) => {
  const [sortConfig, setSortConfig] = useState(null);
  const [filterText, setFilterText] = useState('');
  const [currentPage, setCurrentPage] = useState(0);

  // Filter data
  const filteredData = useMemo(() => {
    if (!filterText) return data;
    return data.filter((row) =>
      columns.some((col) => {
        const value = row[col.key];
        return value && value.toString().toLowerCase().includes(filterText.toLowerCase());
      })
    );
  }, [data, filterText, columns]);

  // Sort data
  const sortedData = useMemo(() => {
    let sorted = [...filteredData];
    if (sortConfig) {
      sorted.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sorted;
  }, [filteredData, sortConfig]);

  // Paginate data
  const paginatedData = useMemo(() => {
    if (disablePagination) {
      return sortedData; // Return all data if pagination is disabled
    }
    const start = currentPage * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, currentPage, pageSize, disablePagination]);

  const totalPages = disablePagination ? 1 : Math.ceil(sortedData.length / pageSize);

  const handleSort = (key) => {
    setSortConfig((current) => {
      if (current?.key === key) {
        return {
          key,
          direction: current.direction === 'asc' ? 'desc' : 'asc'
        };
      }
      return { key, direction: 'asc' };
    });
    setCurrentPage(0);
  };

  if (loading) {
    return <div className="table-loading">Loading data...</div>;
  }

  if (!data || data.length === 0) {
    return (
      <div className="table-empty">
        <p>No data available</p>
      </div>
    );
  }

  return (
    <div className={`data-table-container ${className}`}>
      {filterable && (
        <div className="table-filter">
          <input
            type="text"
            placeholder="Filter rows..."
            value={filterText}
            onChange={(e) => {
              setFilterText(e.target.value);
              setCurrentPage(0);
            }}
            className="filter-input"
          />
          <span className="filter-count">{filteredData.length} results</span>
        </div>
      )}

      <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  onClick={() => sortable && col.sortable !== false && handleSort(col.key)}
                  className={`th-${col.key} ${
                    col.sortable !== false && sortable ? 'sortable' : ''
                  } ${sortConfig?.key === col.key ? `sorted-${sortConfig.direction}` : ''}`}
                >
                  {col.label}
                  {sortable && col.sortable !== false && (
                    <span className="sort-indicator">
                      {sortConfig?.key === col.key
                        ? sortConfig.direction === 'asc'
                          ? ' ↑'
                          : ' ↓'
                        : ' ⇅'}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((row, rowIdx) => (
              <tr
                key={rowIdx}
                className={onRowClick ? 'clickable' : ''}
                onClick={() => onRowClick && onRowClick(row)}
              >
                {columns.map((col) => (
                  <td key={`${rowIdx}-${col.key}`} className={`td-${col.key}`}>
                    {col.render ? col.render(row[col.key], row) : row[col.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="table-pagination">
          <button
            onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
            disabled={currentPage === 0}
            className="btn-prev"
          >
            ← Previous
          </button>
          <span className="page-info">
            Page {currentPage + 1} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={currentPage >= totalPages - 1}
            className="btn-next"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

export default DataTable;
